/**
 * @module @eoln/graphql-client/client-base
 * @description Base class for GraphQL clients with consistent error handling and retry logic.
 */

import { ErrorAuthentication, ErrorAuthorization } from "@eoln/errors";
import { HttpServerError, NetworkError, RequestTimeoutError, RetryManager, type RetryMetrics } from "@eoln/resilience";
import { DEFAULT_RETRY_CONFIG, type GraphQLClientOptions, type GraphQLResponse } from "./types.ts";

// Type alias for Web API HeadersInit (Record<string, string> | [string, string][] | Headers)
type HeadersInitType = Record<string, string>;

/**
 * Abstract base class for GraphQL clients with consistent error handling and retry logic.
 *
 * All SDK clients should extend this class to ensure:
 * - Consistent header format (`x-api-key`)
 * - Consistent error translation to @eoln/errors types
 * - Optional retry with exponential backoff using @eoln/resilience
 * - Type-safe GraphQL requests
 * - Health metrics exposure for monitoring
 *
 * @example
 * ```typescript
 * class MyServiceClient extends GraphQLClientBase {
 *   async getResource(id: string): Promise<Resource> {
 *     const query = `query GetResource($id: ID!) { resource(id: $id) { id name } }`;
 *     const response = await this.graphqlRequest<{ resource: Resource }>(query, { id });
 *     return response.resource;
 *   }
 * }
 *
 * const client = new MyServiceClient({
 *   endpoint: 'https://api.example.com/graphql',
 *   apiKey: 'sk_...',
 * });
 * ```
 */
export abstract class GraphQLClientBase {
  /** GraphQL endpoint URL */
  private readonly _endpoint: string;

  /** Current API key for authentication */
  protected apiKey: string | undefined;

  /**
   * Gets the configured endpoint URL.
   */
  get endpoint(): string {
    return this._endpoint;
  }

  /** Retry manager instance (undefined if retry disabled) */
  protected readonly retryManager: RetryManager | undefined;

  /** Additional headers to include in requests */
  protected readonly headers: Record<string, string>;

  /** Per-attempt upstream timeout; a hung upstream must not hold the caller's whole budget. */
  protected readonly timeoutMs: number | undefined;

  /**
   * Creates a new GraphQL client instance.
   *
   * @param options - Client configuration options
   */
  constructor(options: GraphQLClientOptions) {
    this._endpoint = options.endpoint;
    this.apiKey = options.apiKey;
    this.headers = options.headers ?? {};
    // No default. A blanket timeout here was an antipattern: 10s aborted
    // legitimate KSeF submissions, and "fixing" it by raising the number to
    // 60s was a guess that bounded nothing real. A timeout must come from a
    // measured p99 for the specific call, so callers opt in per client.
    this.timeoutMs = options.timeoutMs;

    // Initialize retry manager if retry config provided and not disabled
    if (options.retry !== false) {
      const retryConfig = options.retry ?? {};
      this.retryManager = new RetryManager({
        maxAttempts: retryConfig.maxAttempts ?? DEFAULT_RETRY_CONFIG.maxAttempts,
        initialDelayMs: retryConfig.initialDelayMs ?? DEFAULT_RETRY_CONFIG.initialDelayMs,
        maxDelayMs: retryConfig.maxDelayMs ?? DEFAULT_RETRY_CONFIG.maxDelayMs,
        backoffMultiplier: retryConfig.backoffMultiplier ?? DEFAULT_RETRY_CONFIG.backoffMultiplier,
        jitter: retryConfig.jitter ?? DEFAULT_RETRY_CONFIG.jitter,
        name: this.constructor.name,
      });
    }
  }

  /**
   * Sets the API key for subsequent requests.
   *
   * @param apiKey - The API key to use for authentication
   */
  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  /**
   * Gets the currently configured API key.
   *
   * @returns The current API key or undefined if not set
   */
  getConfiguredApiKey(): string | undefined {
    return this.apiKey;
  }

  /**
   * Gets health metrics for monitoring retry behavior.
   * Required for all SDK clients using resilience.
   *
   * @returns Object containing retry metrics
   */
  getHealthMetrics(): { retry: RetryMetrics } {
    return {
      retry: this.retryManager?.getMetrics() ?? {
        totalAttempts: 0,
        totalSuccesses: 0,
        totalFailures: 0,
        lastAttemptTime: null,
      },
    };
  }

  /**
   * Makes a GraphQL request with optional retry logic.
   *
   * @param query - GraphQL query or mutation string
   * @param variables - Optional query variables
   * @returns Parsed response data
   * @throws {ErrorAuthentication} For UNAUTHENTICATED errors
   * @throws {ErrorAuthorization} For FORBIDDEN errors
   * @throws {NetworkError} For network connectivity issues
   * @throws {HttpServerError} For HTTP 5xx errors
   * @throws {Error} For other GraphQL errors
   */
  protected async graphqlRequest<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    if (this.retryManager) {
      return this.retryManager.execute(
        () => this.executeGraphQL<T>(query, variables),
        (error) => this.isRetryableError(error),
      );
    }
    return this.executeGraphQL<T>(query, variables);
  }

  /**
   * Executes a single GraphQL request.
   *
   * @param query - GraphQL query or mutation string
   * @param variables - Optional query variables
   * @returns Parsed response data
   */
  private async executeGraphQL<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const headers: HeadersInitType = {
      "Content-Type": "application/json",
      ...this.headers,
    };

    // Standardized header: lowercase x-api-key
    if (this.apiKey) {
      headers["x-api-key"] = this.apiKey;
    }

    let response: Response;

    try {
      response = await fetch(this.endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ query, variables }),
        ...(this.timeoutMs === undefined ? {} : { signal: AbortSignal.timeout(this.timeoutMs) }),
      });
    } catch (error) {
      if (error instanceof Error && (error.name === "TimeoutError" || error.name === "AbortError")) {
        // NOT a NetworkError: the upstream may still be executing this
        // request, so a retry re-sends it. isRetryableError refuses this type.
        throw new RequestTimeoutError(`Request to ${this.endpoint} timed out after ${this.timeoutMs}ms`);
      }
      throw new NetworkError(error instanceof Error ? error.message : "Network request failed");
    }

    // Handle HTTP 5xx server errors before parsing
    if (response.status >= 500) {
      throw new HttpServerError(`Server error: ${response.status}`, response.status);
    }

    // Parse response - GraphQL returns errors in body even for 4xx
    const result = (await response.json()) as GraphQLResponse<T>;

    if (result.errors) {
      const error = result.errors[0];

      if (error.extensions?.code === "UNAUTHENTICATED") {
        throw new ErrorAuthentication(error.message);
      }

      if (error.extensions?.code === "FORBIDDEN") {
        throw new ErrorAuthorization(error.message, error.extensions?.requiredScopes ?? []);
      }

      throw new Error(error.message);
    }

    if (!result.data) {
      throw new Error("GraphQL response missing data field");
    }

    return result.data;
  }

  /**
   * Determines if an error should trigger a retry.
   * Override in subclasses for custom retry logic.
   *
   * Default behavior:
   * - Retry: NetworkError, HttpServerError
   * - Don't retry: RequestTimeoutError (may still be executing upstream),
   *   ErrorAuthentication, ErrorAuthorization, other errors
   *
   * @param error - The error to evaluate
   * @returns true if the error should trigger a retry
   */
  protected isRetryableError(error: unknown): boolean {
    // A timeout is NOT retryable: the request may still be running upstream,
    // so re-sending it executes the mutation twice. Checked first, because a
    // subclass widening the retryable set must not accidentally re-admit it.
    if (error instanceof RequestTimeoutError) {
      return false;
    }

    // Retry network and server errors
    if (error instanceof NetworkError || error instanceof HttpServerError) {
      return true;
    }

    // Don't retry auth/authorization errors
    if (error instanceof ErrorAuthentication || error instanceof ErrorAuthorization) {
      return false;
    }

    return false;
  }
}
